/**
 * Generic words a visitor appends to a library search that are never part of a
 * sound's name (TES-2229).
 *
 * Real Google queries almost always carry one — `hee hee sound effect`,
 * `bolinha de golfe mp3`, `netflix intro sound download`. `/api/sounds?search=`
 * matches the typed phrase as one contiguous ILIKE substring, so a single extra
 * word that is not in the title fails the whole predicate and the visitor who
 * arrived from that exact Google query gets an empty library. 49 of the 59
 * real-GSC fixtures in app/api/sounds/__tests__/fixtures missed this way,
 * 13,405 impressions/28d.
 *
 * Every word below was measured one at a time against the live catalogue on
 * 2026-09-24 (scripts/qa/tes-2229-search-fixture-probe.mjs --words) rather than
 * adopted from a plausible-looking list. Four carry the whole effect — `sound`
 * (16 fixtures alone), `mp3` (1), `meme` (1), `effect` (1) — and the remaining
 * entries are the plural/variant spellings of those same four, which no fixture
 * happens to exercise but which are the identical class of word.
 *
 * Deliberately NOT here:
 *
 *  - `tesla` and `lock` — the issue flags them as load-bearing on some pages,
 *    and they rescued 0 fixtures, so there is no measurement that would justify
 *    the risk;
 *  - `audio`, `noise`, `tone` — 0 fixtures each, and each is a plausible real
 *    content word ("white noise", "dial tone"). A filler list earns entries by
 *    measurement, not by sounding generic.
 */
const FILLER_WORDS = new Set([
  'sound',
  'sounds',
  'effect',
  'effects',
  'download',
  'downloads',
  'mp3',
  'wav',
  'free',
  'ringtone',
  'meme',
])

/** Matches `normalizeSearchTerm` in app/api/sounds/route.ts. */
function normalizeToken(token: string): string {
  return token.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')
}

/**
 * The typed query with generic filler words dropped, or `null` when there is
 * nothing useful to retry with.
 *
 * `null` in two cases, and both matter:
 *
 *  - nothing was stripped — retrying the identical term is a wasted round trip
 *    against a query we already know returns zero rows;
 *  - everything was stripped — `sound effect`, `free mp3 download`. An empty
 *    term would relax to `%%`, which matches every row, and the fallback would
 *    degrade into "here is the whole catalogue" exactly where the visitor
 *    deserves the real empty state.
 *
 * Whole tokens only. `lockchime` is one token and survives intact; only a
 * free-standing `lock` would ever be a candidate, and `lock` is not on the list.
 */
export function stripFillerWords(search: string): string | null {
  const tokens = search.split(/\s+/).filter(Boolean)
  const kept = tokens.filter((token) => !FILLER_WORDS.has(normalizeToken(token)))

  if (kept.length === 0 || kept.length === tokens.length) return null
  return kept.join(' ')
}

/** Exported for the per-token regression tests; not a runtime dependency. */
export const SEARCH_FILLER_WORDS: readonly string[] = [...FILLER_WORDS]
